import { datadogLogs } from '@datadog/browser-logs'
import { INTEGRATION_TYPE } from '@lightspeed-payments/payments-terminal-sdk'
import { ApiRetailerPaymentType } from '@vend/types'
import uuid from '@vend/vend-uuid'
import { round, vn } from 'vend-number'
import {
  canApplySurcharge,
  getTipEligibleAmount,
} from '../../../../functions/payment-types/lspay'
import {
  getAllPaymentsForPaymentType,
  getPaymentIds,
  hasAnyProcessingServicePayment,
  hasPaymentsForOtherPaymentType,
} from '../../../../functions/payments'
import { isBlindReturn, isReturn } from '../../../../functions/sale'
import {
  useRegisterManager,
  useRetailerSettingsManager,
} from '../../../../ng-compat/hooks'
import { useAppSelector } from '../../../../state/hooks'
import { selectCurrentSale } from '../../../../state/slices/currentSale/currentSaleSlice'
import { getFeatureFromCache } from '../../../../util/feature-cache'
import { LSPayPaymentOption } from '../PaymentModal/types'
import { SANDBOX_FLAG } from '../../../payments/lspay-payment-modal/helpers/lspayConstants'
import {
  getMonitoringInstance,
  LSPAY_TERMINAL_EVENT,
  sendLSPayTerminalEvent,
} from '../../../payments/lspay-payment-modal/helpers/terminalMonitoring'
import { RecoveryPaymentOptions } from './useTransactionRecovery'

/**
 *
 * @param amountTendered
 * @returns Function to build LSPay payment options for the LSPay payment type modal
 */
export const useHandleLSPay = (amountTendered: string) => {
  const registerManager = useRegisterManager()
  const retailerSettingsManager = useRetailerSettingsManager()
  const sale = useAppSelector(selectCurrentSale)

  const handleLSPay = (
    paymentType: ApiRetailerPaymentType,
    recoveryPaymentOptions?: RecoveryPaymentOptions
  ): LSPayPaymentOption => {
    const activeOutlet = registerManager.getActiveOutlet()
    const lspaySurchargeOn = getFeatureFromCache('lspay_surcharge')
    const hasQBO = getFeatureFromCache('has_qbo')
    const hasXeroEnabled = retailerSettingsManager.isXeroIntegrationEnabled()
    const surchargeEnabledForXero = getFeatureFromCache(
      'lspay_surcharge_xero_onaccount'
    )
    const surchargeEnabledForQbo = getFeatureFromCache(
      'lspay_surcharge_qbo_onaccount'
    )
    const lspayTippingOnPretaxAmount = getFeatureFromCache(
      'lspay_tipping_on_pre_tax_amount'
    )

    const tipAllowed =
      lspayTippingOnPretaxAmount && !isReturn(sale) && !isBlindReturn(sale)

    const couldSurchargeBeApplied = canApplySurcharge(
      amountTendered,
      {
        ...sale,
        status: sale.status,
      },
      hasQBO,
      hasXeroEnabled,
      surchargeEnabledForXero,
      surchargeEnabledForQbo
    )
    const { intentId, transactionId, recoveryAmount } =
      recoveryPaymentOptions || {}

    const options: LSPayPaymentOption = {
      paymentModalType: 'lsPay',
      amount: vn(recoveryAmount || amountTendered).toNumber(),
      outletId: activeOutlet.id,
      currency: activeOutlet.currency,
      register: registerManager.getActiveRegister(),
      // Sale-less ABP flow: when abpTransactionId is set, there is no sale — use it as
      // the LSPay correlation key instead of sale.localId.
      registerSaleId: sale.payAccountBalance?.abpTransactionId ?? sale.localId,
      saleTotal: sale.payAccountBalance?.abpTransactionId
        ? undefined
        : vn(sale.finals.inclusivePrice).toNumber(),
      paymentType,
      transactionId: transactionId || uuid(),
      paymentIntentId: intentId,
      isRecovery: !!transactionId,
      withSurcharge: lspaySurchargeOn && couldSurchargeBeApplied,
      tipEligibleAmount:
        tipAllowed && !sale.payAccountBalance?.abpTransactionId
          ? vn(round(getTipEligibleAmount(amountTendered, sale), 2)).toNumber()
          : undefined,
      customerId: sale?.customer?.id,
    }

    // A recovery attempt arrived but isRecovery is false, so a new transactionId
    // and payment intent are created instead of recovering the original.
    if (recoveryPaymentOptions && !options.isRecovery) {
      const recoveryContext = {
        registerSaleId: options.registerSaleId,
        originalRegisterSalePaymentId: (recoveryPaymentOptions as {
          registerSalePaymentId?: string
        }).registerSalePaymentId,
        recoveryPaymentIntentId: intentId,
        newTransactionId: options.transactionId,
      }
      sendLSPayTerminalEvent(
        getMonitoringInstance(
          activeOutlet.id,
          getFeatureFromCache(SANDBOX_FLAG),
          INTEGRATION_TYPE.CLOUD
        ),
        LSPAY_TERMINAL_EVENT.RECOVERY_DEGRADED,
        recoveryContext
      )
    }

    if (
      !!recoveryAmount &&
      !!amountTendered &&
      !vn(recoveryAmount).eq(vn(amountTendered))
    ) {
      datadogLogs.logger.info('LSPay Recovery Amount Override Applied', {
        recoveryAmount,
        amountTendered,
        paymentOptions: options,
      })
    }

    if (isReturn(sale)) {
      const originalRegisterSalePayments = getAllPaymentsForPaymentType({
        payments: sale.originalSalePayments,
        paymentTypeId: paymentType.type_id,
      })

      options.originalRegisterSalePaymentIds = getPaymentIds(
        originalRegisterSalePayments
      )
      options.originalSalePaidByOtherPT = hasPaymentsForOtherPaymentType({
        payments: sale.originalSalePayments,
        paymentTypeId: paymentType.type_id,
      })
      // Calculate total amount from original LSPay payments for split-tender detection
      options.originalLspayPaymentAmount = originalRegisterSalePayments.reduce(
        (total, payment) => total + Number(payment.amount),
        0
      )
      // Check if any of the original payments were processed via Processing Service.
      // This flag ensures refunds use LSPAYRefundClientDriven component with the legacy API endpoint,
      // as the server-driven API endpoint doesn't support Processing Service refunds yet.
      options.originalSaleHasProcessingServicePayment = hasAnyProcessingServicePayment(
        originalRegisterSalePayments
      )
    }

    return options
  }

  return {
    handleLSPay,
  }
}
